const THRESHOLD = 3.5;
// Smoke 0.30 vs vape 0.32 is negligible; one inhale chip uses the higher (conservative) value.
const BIO = { inhale: 0.32, smoke: 0.32, vapo: 0.32, edible: 0.06 };
const PEAK_SCALE = 445;
const SEX_FACTOR = { m: 1.0, w: 1.15, d: 1.0 };
const K2_H = { once: 20, occ: 26, reg: 34, daily: 46 };
const DEPOT0 = { once: 0, occ: 0.8, reg: 2.2, daily: 4.0 };
const DEPOT_H = { once: 40, occ: 90, reg: 150, daily: 240 };
const A_FAST = 0.7;
const DIST_H = 0.5;
const ORAL_ABS_H = 0.75;
const CORRIDOR = { low: 0.65, high: 1.55 };
const COOH_KF = 0.09;
const COOH_H = { once: 24, occ: 36, reg: 60, daily: 96 };
const URINE_DAYS = { once: 4, occ: 7, reg: 28, daily: 56 };
const LN2 = Math.LN2;
const SAFE_SEARCH_HOURS = 24 * 30;

const interpolate = (template, vars) => {
  if (!template) return '';

  return Object.keys(vars).reduce((text, key) => {
    return text.split(`{${key}}`).join(String(vars[key]));
  }, template);
};

const derive = (inp) => {
  const sexF = SEX_FACTOR[inp.sex] || 1.0;
  const form = inp.form === 'edible' ? 'edible' : 'inhale';
  const doseMg = form === 'edible'
    ? inp.pieces * inp.mg_per_piece
    : inp.amount_g * (inp.thc_pct / 100) * 1000;
  const absorbed = Math.max(0, doseMg) * (BIO[form] || BIO.inhale);

  return {
    isOral: form === 'edible',
    doseMg: Math.max(0, doseMg),
    pk: absorbed * PEAK_SCALE / inp.weight_kg,
    k2: K2_H[inp.freq] * sexF,
    d0: DEPOT0[inp.freq],
    dh: DEPOT_H[inp.freq] * sexF,
    coohH: COOH_H[inp.freq]
  };
};

const serumTHC = (t, p) => {
  const depot = p.d0 * Math.exp(-LN2 / p.dh * t);

  if (p.isOral) {
    const ka = LN2 / ORAL_ABS_H;
    let ke = LN2 / p.k2;
    if (Math.abs(ka - ke) < 1e-6) ke = ka * 0.999;
    return p.pk * (ka / (ka - ke)) * (Math.exp(-ke * t) - Math.exp(-ka * t)) + depot;
  }

  return p.pk * (A_FAST * Math.exp(-LN2 / DIST_H * t) + (1 - A_FAST) * Math.exp(-LN2 / p.k2 * t)) + depot;
};

const peakTime = (p) => {
  if (!p.isOral) return 0;

  const ka = LN2 / ORAL_ABS_H;
  let ke = LN2 / p.k2;
  if (Math.abs(ka - ke) < 1e-6) ke = ka * 0.999;
  return Math.log(ka / ke) / (ka - ke);
};

const conservativeTHC = (t, p) => serumTHC(t, p) * CORRIDOR.high;

const findTSafeHigh = (hoursSince, p, threshold) => {
  const tPeak = peakTime(p);
  const start = Math.max(hoursSince, tPeak);
  const limit = hoursSince + SAFE_SEARCH_HOURS;

  if (conservativeTHC(start, p) < threshold) return start;

  let lo = start;
  let hi = start + 1;

  while (hi < limit && conservativeTHC(hi, p) >= threshold) {
    hi = Math.min(limit, hi * 2);
  }

  if (conservativeTHC(hi, p) >= threshold) return null;

  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    if (conservativeTHC(mid, p) >= threshold) lo = mid;
    else hi = mid;
  }

  return hi;
};

const formatDisplayDate = (hoursFromNow, locale) => {
  const target = new Date(Date.now() + hoursFromNow * 3600 * 1000);
  return target.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatNg = (value, locale) => {
  return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const pointsToPath = (points) => {
  if (points.length === 0) return '';

  return points.map((point, index) => {
    const command = index === 0 ? 'M' : 'L';
    return `${command}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
  }).join(' ');
};

const bandPath = (highPoints, lowPoints) => {
  if (highPoints.length === 0) return '';

  const reverseLow = [...lowPoints].reverse();
  const start = `M${highPoints[0].x.toFixed(2)},${highPoints[0].y.toFixed(2)}`;
  const highLine = highPoints.slice(1).map((point) => `L${point.x.toFixed(2)},${point.y.toFixed(2)}`).join('');
  const lowLine = reverseLow.map((point) => `L${point.x.toFixed(2)},${point.y.toFixed(2)}`).join('');
  return `${start}${highLine}${lowLine}Z`;
};

const readI18n = (root) => {
  const node = root.querySelector('[data-abbaurechner-i18n]');
  if (!node) return {};

  try {
    return JSON.parse(node.textContent);
  } catch {
    return {};
  }
};

class ThcAbbaurechner extends HTMLElement {
  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.#i18n = readI18n(this);
    this.#locale = this.dataset.locale || document.documentElement.lang || 'de';
    this.#threshold = Number(this.dataset.threshold) || THRESHOLD;
    this.#revealed = false;

    this.#result = this.querySelector('[data-abbaurechner-result]');
    this.#head = this.querySelector('[data-result-head]');
    this.#value = this.querySelector('[data-result-value]');
    this.#line = this.querySelector('[data-result-line]');
    this.#warning = this.querySelector('[data-result-warning]');
    this.#gateCheckbox = this.querySelector('[data-gate-checkbox]');
    this.#gateButton = this.querySelector('[data-gate-button]');
    this.#hoursInput = this.querySelector('[data-hours-input]');
    this.#doseLine = this.querySelector('[data-dose-line]');
    this.#chartSvg = this.querySelector('[data-abbaurechner-chart]');
    this.#chartDetails = this.querySelector('[data-chart-details]');

    this.addEventListener('change', (event) => this.#onChange(event));
    this.addEventListener('input', (event) => this.#onInput(event));
    this.#gateButton?.addEventListener('click', () => this.#reveal());
    this.#syncGateButton();
    this.#syncFormPanels();
    this.#updateDoseLine();
  }

  #ready = false;
  #revealed = false;
  #i18n = {};
  #locale = 'de';
  #threshold = THRESHOLD;
  #result = null;
  #head = null;
  #value = null;
  #line = null;
  #warning = null;
  #gateCheckbox = null;
  #gateButton = null;
  #hoursInput = null;
  #doseLine = null;
  #chartSvg = null;
  #chartDetails = null;

  #onChange(event) {
    const target = event.target;
    const field = target?.dataset?.field;
    const custom = target?.dataset?.custom;

    if (field === 'form') this.#syncFormPanels();
    if (field === 'hours') this.#syncHoursFromChip(target.value);
    if (field && ['amount', 'strength', 'pieces', 'mg'].includes(field)) {
      this.#syncCustomFromChip(field, target.value);
    }

    if (custom) this.#syncChipFromCustom(custom, target.value);

    this.#syncGateButton();
    this.#updateDoseLine();
    if (this.#revealed) this.#render();
  }

  #onInput(event) {
    const target = event.target;
    if (!target) return;

    this.#syncGateButton();

    if (target === this.#hoursInput) {
      this.#syncChipFromCustom('hours', target.value);
      if (this.#revealed) this.#render();
      return;
    }

    if (target.dataset?.field === 'weight') {
      if (this.#revealed) this.#render();
      return;
    }

    const custom = target.dataset?.custom;
    if (!custom) return;

    this.#syncChipFromCustom(custom, target.value);
    this.#updateDoseLine();
    if (this.#revealed) this.#render();
  }

  #syncFormPanels() {
    const form = this.querySelector('[data-field="form"]:checked')?.value || 'inhale';
    const showEdible = form === 'edible';

    this.querySelectorAll('[data-panel="flower"]').forEach((panel) => {
      panel.hidden = showEdible;
    });
    this.querySelectorAll('[data-panel="edible"]').forEach((panel) => {
      panel.hidden = !showEdible;
    });
  }

  #syncHoursFromChip(value) {
    if (this.#hoursInput) this.#hoursInput.value = value;
  }

  #syncCustomFromChip(field, value) {
    const custom = this.querySelector(`[data-custom="${field}"]`);
    if (custom) custom.value = value;
  }

  #syncChipFromCustom(field, rawValue) {
    const parsed = Number(rawValue);

    this.querySelectorAll(`[data-field="${field}"]`).forEach((chip) => {
      chip.checked = Number.isFinite(parsed) && Number(chip.value) === parsed;
    });
  }

  #readNumeric(field, fallback) {
    const custom = this.querySelector(`[data-custom="${field}"]`);
    if (custom && custom.value !== '') {
      return this.#readBoundedNumber(custom, fallback);
    }

    const chip = this.querySelector(`[data-field="${field}"]:checked`);
    if (chip) {
      const parsed = Number(chip.value);
      if (Number.isFinite(parsed)) return parsed;
    }

    return fallback;
  }

  #readBoundedNumber(input, fallback) {
    if (!input || input.value === '') return fallback;

    const parsed = Number(input.value);
    if (!Number.isFinite(parsed)) return fallback;

    const min = input.min === '' ? -Infinity : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    return Math.min(max, Math.max(min, parsed));
  }

  #formatAmount(value) {
    return value.toLocaleString(this.#locale, {
      maximumFractionDigits: value % 1 === 0 ? 0 : 2
    });
  }

  #syncGateButton() {
    if (!this.#gateButton || !this.#gateCheckbox) return;

    const hasInvalidInput = [...this.querySelectorAll('input[type="number"]')]
      .some((input) => !input.checkValidity());
    this.#gateButton.disabled = !this.#gateCheckbox.checked || hasInvalidInput;
  }

  #reveal() {
    if (!this.#gateCheckbox?.checked) return;
    this.#revealed = true;
    this.#render();
    this.#scrollToResult();
  }

  #scrollToResult() {
    if (!this.#result) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#result.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center'
    });
  }

  #setValue(text) {
    if (!this.#value) return;
    if (!text) {
      this.#value.textContent = '';
      this.#value.hidden = true;
      return;
    }

    this.#value.textContent = text;
    this.#value.hidden = false;
  }

  #readInput() {
    const selected = (field) => this.querySelector(`[data-field="${field}"]:checked`)?.value;
    const hours = this.#readBoundedNumber(this.#hoursInput, Number(selected('hours') || 1));

    return {
      goal: selected('goal') || 'blood',
      form: selected('form') === 'edible' ? 'edible' : 'inhale',
      amount_g: this.#readNumeric('amount', 0.3),
      thc_pct: this.#readNumeric('strength', 18),
      pieces: this.#readNumeric('pieces', 1),
      mg_per_piece: this.#readNumeric('mg', 10),
      hours_since: hours,
      freq: selected('freq') || 'once',
      weight_kg: this.#readBoundedNumber(this.querySelector('[data-field="weight"]'), 75),
      sex: selected('sex') || 'm'
    };
  }

  #updateDoseLine() {
    if (!this.#doseLine) return;
    const inp = this.#readInput();
    const p = derive(inp);
    const mg = this.#formatAmount(p.doseMg);

    if (inp.form === 'edible') {
      this.#doseLine.textContent = interpolate(this.#i18n.doseEdible, {
        pieces: this.#formatAmount(inp.pieces),
        mg: this.#formatAmount(inp.mg_per_piece),
        total: mg
      });
      return;
    }

    this.#doseLine.textContent = interpolate(this.#i18n.doseFlower, { mg });
  }

  #render() {
    const inp = this.#readInput();
    if (!inp.weight_kg || inp.weight_kg <= 0) return;

    const p = derive(inp);

    if (inp.goal === 'urine') {
      this.#renderUrine(inp, p);
      this.#drawChart('urine', inp, p);
      return;
    }

    this.#renderBlood(inp, p);
    this.#drawChart('blood', inp, p);
  }

  #setState(state) {
    if (!this.#result) return;
    this.#result.dataset.state = state;
    this.#result.classList.remove(
      'abbaurechner__result--idle',
      'abbaurechner__result--over',
      'abbaurechner__result--under',
      'abbaurechner__result--urine'
    );
    this.#result.classList.add(`abbaurechner__result--${state}`);
  }

  #renderBlood(inp, p) {
    const nowValue = Math.max(0, serumTHC(inp.hours_since, p));
    const tPeak = peakTime(p);
    const peakValue = Math.max(0, serumTHC(tPeak, p));
    const stillRising = p.isOral && inp.hours_since < tPeak;
    const willCross = peakValue >= this.#threshold;
    const isOver = nowValue >= this.#threshold || (stillRising && willCross);

    this.#setState(isOver ? 'over' : 'under');

    if (this.#head) {
      this.#head.textContent = isOver ? this.#i18n.resultOverHead : this.#i18n.resultUnderHead;
    }

    const value = formatNg(nowValue, this.#locale);
    this.#setValue(interpolate(this.#i18n.resultValueNg, { value }));

    if (this.#line) {
      if (!isOver) {
        this.#line.textContent = interpolate(this.#i18n.resultUnderLine, { value });
      } else {
        this.#line.textContent = interpolate(this.#i18n.resultOverLine, { value });
      }
    }

    if (this.#warning) {
      this.#warning.textContent = isOver ? this.#i18n.resultOverWarning : this.#i18n.resultUnderWarning;
    }
  }

  #renderUrine(inp, p) {
    const remainingH = URINE_DAYS[inp.freq] * 24 - inp.hours_since;
    this.#setState('urine');

    if (this.#head) this.#head.textContent = this.#i18n.resultUrineHead;
    this.#setValue('');

    if (this.#line) {
      if (remainingH > 0) {
        const date = formatDisplayDate(remainingH, this.#locale);
        this.#line.textContent = interpolate(this.#i18n.resultUrineLine, { date });
      } else {
        this.#line.textContent = this.#i18n.resultUrineExpired;
      }
    }

    if (this.#warning) this.#warning.textContent = this.#i18n.resultUrineWarning;
  }

  #drawChart(goal, inp, p) {
    const svg = this.#chartSvg;
    if (!svg) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const width = 320;
    const height = 180;
    const pad = { top: 16, right: 12, bottom: 28, left: 36 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;

    const tSafeHigh = goal === 'blood' ? findTSafeHigh(inp.hours_since, p, this.#threshold) : null;
    const tMax = goal === 'urine'
      ? Math.max(URINE_DAYS[inp.freq] * 24 * 1.15, inp.hours_since + 24, 48)
      : Math.min(240, Math.max(48, inp.hours_since + 12, (tSafeHigh || 24) + 8, peakTime(p) + 12));

    const steps = 80;
    const dt = tMax / steps;
    const samples = [];

    if (goal === 'blood') {
      for (let i = 0; i <= steps; i += 1) {
        const t = i * dt;
        const mid = Math.max(0, serumTHC(t, p));
        samples.push({
          t,
          mid,
          low: mid * CORRIDOR.low,
          high: mid * CORRIDOR.high
        });
      }
    } else {
      const coohDt = Math.max(p.coohH / 130, tMax / 200);
      let cooh = 0;
      let t = 0;
      samples.push({ t: 0, mid: 0, low: 0, high: 0 });

      while (t < tMax) {
        const thc = Math.max(0, serumTHC(t, p));
        cooh = Math.max(0, cooh + (COOH_KF * thc - LN2 / p.coohH * cooh) * coohDt);
        t += coohDt;
        samples.push({ t, mid: cooh, low: cooh, high: cooh });
      }
    }

    const yMax = Math.max(
      ...samples.map((sample) => sample.high),
      goal === 'blood' ? this.#threshold : 0,
      0.1
    );

    const x = (t) => pad.left + (t / tMax) * innerW;
    const y = (value) => pad.top + innerH - (value / yMax) * innerH;

    const midPts = samples.map((sample) => ({ x: x(sample.t), y: y(sample.mid) }));
    const highPts = samples.map((sample) => ({ x: x(sample.t), y: y(sample.high) }));
    const lowPts = samples.map((sample) => ({ x: x(sample.t), y: y(sample.low) }));

    const nowX = x(Math.min(inp.hours_since, tMax));
    const thresholdY = y(this.#threshold);

    const axis = `<line x1="${pad.left}" y1="${pad.top + innerH}" x2="${pad.left + innerW}" y2="${pad.top + innerH}" stroke="currentColor" stroke-opacity="0.25"/>
      <line x1="${pad.left}" y1="${pad.top}" x2="${pad.left}" y2="${pad.top + innerH}" stroke="currentColor" stroke-opacity="0.25"/>
      <text x="${pad.left + innerW}" y="${height - 8}" text-anchor="end" font-size="10" fill="currentColor" fill-opacity="0.65">${Math.round(tMax)} h</text>
      <text x="${pad.left}" y="${height - 8}" font-size="10" fill="currentColor" fill-opacity="0.65">0</text>`;

    const thresholdLine = goal === 'blood'
      ? `<line x1="${pad.left}" y1="${thresholdY}" x2="${pad.left + innerW}" y2="${thresholdY}" stroke="currentColor" stroke-dasharray="4 4" stroke-opacity="0.45"/>
         <text x="${pad.left + 4}" y="${thresholdY - 4}" font-size="10" fill="currentColor" fill-opacity="0.7">${this.#threshold} ng/ml</text>`
      : '';

    const band = goal === 'blood' ? `<path d="${bandPath(highPts, lowPts)}" fill="currentColor" fill-opacity="0.08"/>` : '';
    const curve = `<path d="${pointsToPath(midPts)}" fill="none" stroke="currentColor" stroke-opacity="0.55" stroke-width="1.75"/>`;
    const nowLine = `<line x1="${nowX}" y1="${pad.top}" x2="${nowX}" y2="${pad.top + innerH}" stroke="currentColor" stroke-opacity="0.35"/>`;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.innerHTML = `${band}${thresholdLine}${curve}${nowLine}${axis}`;

    if (this.#chartDetails && !reduceMotion) {
      this.#chartDetails.style.scrollMarginTop = '0';
    }
  }
}

if (!customElements.get('thc-abbaurechner')) {
  customElements.define('thc-abbaurechner', ThcAbbaurechner);
}
